import { useTranslation } from '@dagens/frontend-i18n';
import { roundNumber } from '@dagensmat/core';
import { add, dinero, toSnapshot } from 'dinero.js';
import { isSameDay } from 'date-fns';
import { DeliveryFeesResponse } from '../../../types/Basket';
import { useAppSelector } from '../../../_common/hooks/reduxHooks';
import { GetDeliveryTypesResponse } from '../../../api';
import { DeliveryType } from '../../../types/Logistics';
import {
  FooterContainer,
  SystemFeedbackContainer
} from './CheckoutFooter.style';
import CheckoutFooterDateLine from './CheckoutFooterDateLine';
import Container from '@carrot-deprecated/container';
import Typography from '@carrot-deprecated/typography';
import { addMany, toMinorUnit, zeroDinero } from 'utils/money';
import REQ, { ReqType } from 'utils/REQ';
import { fmtMoney } from 'utils/texts';

import { ActionButton } from '@components/action-button';
import {
  getBasketByProducer,
  ProductWithBasket,
  sumPriceForProductsInBasket
} from '_consumer/reducers/productsForSale';

type CheckoutButtonProps = {
  isApprovedToOrder: boolean;
  submitReq: ReqType;
  placeOrder: () => void;
  deliveryDateCount: number;
  disabled: boolean;
};

const CheckoutButton = ({
  isApprovedToOrder,
  submitReq,
  placeOrder,
  deliveryDateCount,
  disabled
}: CheckoutButtonProps) => {
  const { t } = useTranslation();
  if (!isApprovedToOrder) {
    return (
      <SystemFeedbackContainer>
        <Typography variant="paragraphSmall" as="span" color="error">
          {t('consumer:MissingOrderRights')}
        </Typography>
      </SystemFeedbackContainer>
    );
  }

  return (
    <ActionButton.Save
      onClick={placeOrder}
      saveReq={submitReq}
      feedbackText={{
        [REQ.PENDING]: t('consumer:SendingOrder'),
        [REQ.ERROR]: t('consumer:SomethingWentWrongTryAgain')
      }}
      disabled={disabled}
    >
      {t('consumer:SendInYourOrder', {
        count: deliveryDateCount
      })}
    </ActionButton.Save>
  );
};

type CheckoutFooterProps = {
  isApprovedToOrder: boolean;
  submitReq: ReqType;
  placeOrder: () => void;
  basketByDeliveryDate: {
    deliveryDate: string;
    products: ProductWithBasket[];
  }[];
  deliveryFees: DeliveryFeesResponse | undefined;
  deliveryTypes: GetDeliveryTypesResponse | undefined;
  newExemptions: string[];
  isDanish: boolean;
};

const CheckoutFooter = ({
  isApprovedToOrder,
  submitReq,
  placeOrder,
  basketByDeliveryDate,
  deliveryFees,
  deliveryTypes,
  newExemptions,
  isDanish
}: CheckoutFooterProps) => {
  const { t } = useTranslation();
  const { basketProducers } = useAppSelector(({ producers }) => {
    return {
      basketProducers: producers.items
    };
  });

  const dayTotals = deliveryFees
    ? basketByDeliveryDate.map(({ deliveryDate, products }) => {
        const basketByProducer = getBasketByProducer(products);
        const minimumOrderAmountFeedback = basketByProducer.map(
          ({ producer: { _id: producerId } }) => {
            const producer = basketProducers.find(({ _id }) => {
              return _id === producerId;
            });

            const minimumOrderAmount = producer?.minimumOrderAmount;
            const subtotal = sumPriceForProductsInBasket(products);
            const underMinimum =
              minimumOrderAmount !== undefined && subtotal < minimumOrderAmount;
            return {
              error: underMinimum && producer?.minimumOrderAmountEnforced,
              warning: underMinimum
            };
          }
        );

        const error = minimumOrderAmountFeedback.some(({ error }) => error);
        const warning = minimumOrderAmountFeedback.some(
          ({ warning }) => warning
        );

        const uniqueProducers = new Map(
          products.map(({ producer }) => [
            producer._id,
            basketProducers.find(({ _id }) => _id === producer._id)
          ])
        );
        const deliveryFeesForProducers = [...uniqueProducers.entries()]
          .map(([producerId, producer]) => {
            const deliveryType = deliveryTypes?.find(deliveryType => {
              return (
                deliveryType.producerId === producerId &&
                isSameDay(deliveryType.date, deliveryDate) &&
                deliveryType.deliveryType === DeliveryType.SELF_DELIVERY
              );
            });
            if (!deliveryType) {
              return 0;
            }
            return producer?.defaultDeliveryFee?.price ?? 0;
          })
          .reduce((acc, fee) => acc + fee, 0);

        const feeToPayToday = deliveryFees.deliveryDayFees.find(day => {
          return (
            isSameDay(day.date, deliveryDate) &&
            day.exemption === '' &&
            !newExemptions.includes(day.date)
          );
        });

        const dagensFeeToPayForDate = feeToPayToday
          ? dinero(feeToPayToday.total)
          : zeroDinero(deliveryFees.totalDeliveryFees.currency);

        const producerDeliveryFeesTotal = dinero({
          amount: roundNumber(toMinorUnit(deliveryFeesForProducers)),
          currency: toSnapshot(dagensFeeToPayForDate).currency
        });

        const dayBasketSum = dinero({
          amount: roundNumber(
            toMinorUnit(sumPriceForProductsInBasket(products))
          ),
          currency: toSnapshot(dagensFeeToPayForDate).currency
        });

        const dayTotal = add(
          add(dayBasketSum, dagensFeeToPayForDate),
          producerDeliveryFeesTotal
        );
        return {
          deliveryDate,
          dayTotal,
          error,
          warning: error ? false : warning
        };
      })
    : [];

  const allDaysTotal = addMany(
    dayTotals.map(({ dayTotal }) => {
      return dayTotal;
    })
  );

  return (
    <FooterContainer>
      {dayTotals.map(({ deliveryDate, dayTotal, error, warning }) => {
        return (
          <CheckoutFooterDateLine
            key={deliveryDate}
            deliveryDate={deliveryDate}
            dayTotal={dayTotal}
            error={error}
            warning={warning}
          />
        );
      })}
      <Container flex justify="space-between" alignItems="baseline" mb="xs">
        <Typography variant="paragraphSmallBold">
          {t('common:CheckoutTotalExVAT')}
        </Typography>
        <Typography variant="paragraphSmallBold">
          {fmtMoney(allDaysTotal)}
        </Typography>
      </Container>
      <CheckoutButton
        deliveryDateCount={basketByDeliveryDate.length}
        isApprovedToOrder={isApprovedToOrder}
        submitReq={submitReq}
        placeOrder={placeOrder}
        disabled={dayTotals.some(({ error }) => error) || isDanish}
      />
    </FooterContainer>
  );
};

export default CheckoutFooter;
